import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { bookingRoute } from "/imports/ui/components/discovery";
import { checkoutRulesForCartItem } from "/imports/libs/bookingRules";
import api from "/imports/api/client";
import { track } from "/imports/libs/analytics";

import useCheckoutPricing from "./useCheckoutPricing";
import CheckoutItemCard from "./components/CheckoutItemCard";
import TermsCheckbox from "/imports/ui/components/shared/TermsCheckbox";
import CheckoutCreditsPanel, { hasCreditsToApply } from "./components/CheckoutCreditsPanel";
import CheckoutPoliciesPanel from "./components/CheckoutPoliciesPanel";
import CheckoutOrderSummary from "./components/CheckoutOrderSummary";
import CheckoutSplitPanel, { getSeatLimits } from "./components/CheckoutSplitPanel";

// A booking can be rejected with a 422 AvailabilityError when the chosen slot
// is outside the service's availability schedule — a stale slot the client
// loaded before the day was restricted. The server attaches `{ day, allowedHours }`
// so we can tell the client when the service is actually bookable instead of
// showing the raw internal reason.
function buildAvailabilityMessage(err) {
  const detail = err.data?.data || {};
  const { day, allowedHours } = detail;
  if (Array.isArray(allowedHours) && allowedHours.length > 0) {
    return `This time is no longer available${day ? ` on ${day}` : ""}. You can book this service during: ${allowedHours.join(", ")}. Please choose another slot.`;
  }
  if (day) {
    return `This service isn't available on ${day}. Please choose another day.`;
  }
  return "This time is no longer available. Please choose another slot.";
}

function CheckoutView() {
  const navigate = useNavigate();
  const theme = useTheme();
  // Below md the layout hides the bottom nav on this route and we show a sticky
  // total + CTA bar instead, so the action never scrolls out of reach.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { slug, cart, clearCart, removeFromCart, currency, services, sports, policies, locationPolicies, widget, saas } = useWidget();
  const { isAuthenticated, activeClient, client } = useAuth();

  // Whose booking this is — the active family member, else the account holder.
  const bookingForProfile = activeClient || client;
  const bookingForName = bookingForProfile ? `${bookingForProfile.firstName || ""} ${bookingForProfile.lastName || ""}`.trim() : "";

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const items = cart.items || [];
  const isEmpty = items.length === 0;
  const displayCurrency = items[0]?.currency || currency || "$";
  const primaryItem = items[0] || null;

  // Self-heal a shared-link checkout: the cart lives in memory, so a refresh /
  // bookmark of `?bookishId=…` lands here empty — bounce back to the join
  // resolver to rebuild it. Runs once on mount so clearing the cart later
  // (Clear Cart / post-checkout) doesn't re-trigger it.
  const [searchParams] = useSearchParams();
  const recoverBookishId = searchParams.get("bookishId");
  const recovered = useRef(false);
  useEffect(() => {
    if (recovered.current) return;
    recovered.current = true;
    if (isEmpty && recoverBookishId) {
      navigate(`/${slug}/join/${recoverBookishId}`, { replace: true });
    }
  }, [isEmpty, recoverBookishId, slug, navigate]);

  const pricing = useCheckoutPricing({ slug, primaryItem, items });

  // Once per arrival with something to pay for — the step between picking a
  // slot (and signing in) and submitting.
  const trackedView = useRef(false);
  useEffect(() => {
    if (trackedView.current || !primaryItem) return;
    trackedView.current = true;
    track("checkout_viewed", {
      kind: primaryItem.type,
      service_id: primaryItem.serviceId || null,
      price: primaryItem.price ?? null,
      currency: primaryItem.currency || currency || null
    });
  }, [primaryItem, currency]);

  // Split payment (open game) — court creations only, never link-joins, and
  // only for a club that has the feature switched on (absent flag = off; the
  // server rejects a split either way). The host authorizes a card hold for the
  // FULL standard price, so packages, vouchers and member pricing don't mix
  // with a split (server rejects them).
  // Each court opts in individually — absent reads as off, matching the club
  // flag, so nothing is offered until the court is explicitly ticked.
  const splitService = (services || []).find((sv) => sv._id === primaryItem?.serviceId) || null;
  const canSplit =
    widget?.splitPaymentEnabled === true && splitService?.splitPaymentEnabled === true && primaryItem?.type === "court" && !primaryItem?.bookishId;

  // Seat range comes from the service's sport: a padel court offers 2–4, not 2–22.
  const splitSportIcon = (sports || []).find((sp) => sp._id === splitService?.sportId)?.icon || "";
  const seatLimits = useMemo(() => getSeatLimits(splitSportIcon), [splitSportIcon]);

  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splitSeats, setSplitSeats] = useState(seatLimits.preset);

  // Sport-appropriate preset, and never leave a stale count above the new cap
  // (switching from a football pitch to a padel court would submit 10 seats).
  useEffect(() => {
    setSplitSeats((n) => (n > seatLimits.max || n < seatLimits.min ? seatLimits.preset : n));
  }, [seatLimits]);
  const splitTotal = parseFloat(primaryItem?.price) || 0;
  const splitShare = splitSeats > 0 ? Math.floor((splitTotal / splitSeats) * 100) / 100 : 0;
  const handleSplitToggle = (on) => {
    setSplitEnabled(on);
    pricing.setSplitMode(on);
  };
  const displayTotal = splitEnabled ? splitTotal : pricing.total;

  const creditsPanelProps = {
    isAuthenticated,
    packagesLoading: pricing.packagesLoading,
    vouchersLoading: pricing.vouchersLoading,
    packages: pricing.packages,
    vouchers: pricing.vouchers,
    membership: pricing.membership,
    membershipApplies: pricing.membershipApplies,
    membershipSaving: pricing.membershipSaving,
    isPackageApplied: pricing.isPackageApplied,
    selectedPackageId: pricing.selectedPackageId,
    setSelectedPackageId: pricing.setSelectedPackageId,
    selectedVoucherId: pricing.selectedVoucherId,
    setSelectedVoucherId: pricing.setSelectedVoucherId,
    primaryAfterPackage: pricing.primaryAfterPackage,
    displayCurrency
  };

  const resolveSportIcon = (item) => {
    const service = (services || []).find((s) => s._id === item.serviceId);
    const sport = service && (sports || []).find((s) => s._id === service.sportId);
    return sport?.icon || null;
  };

  // Return to the booking flow the cart's primary item came from, falling back
  // to home when the category can't be resolved.
  const handleBack = () => {
    const primaryService = (services || []).find((s) => s._id === primaryItem?.serviceId);
    const route = bookingRoute(primaryService);
    navigate(route ? `/${slug}/${route}` : `/${slug}`);
  };

  // Removing the last item empties the cart, and a review page with nothing to
  // review is a dead end — the client has to find their own way back. Send them
  // where Clear Cart does, since it is the same outcome reached a different way.
  const handleRemoveItem = (index) => {
    removeFromCart(index);
    if (items.length <= 1) navigate(`/${slug}`);
  };

  // Clearing the cart is destructive-ish (rebuilding a booking takes several
  // taps), so it sits behind a confirm dialog instead of firing on one tap.
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const handleClearCart = () => {
    setConfirmClearOpen(false);
    clearCart();
    navigate(`/${slug}`);
  };

  const handleCheckout = async () => {
    const item = items[0];
    if (!item) return;
    // The button is disabled without it; this guards the programmatic path too.
    if (!termsAccepted) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await api.createBooking(slug, {
        type: item.type,
        bookishId: item.bookishId || null,
        serviceId: item.serviceId,
        pricingId: item.pricingId,
        date: item.date,
        time: item.time,
        duration: item.duration,
        facilityId: item.facilityId || null,
        facilityAuto: !!item.facilityAuto,
        trainerId: item.trainerId || null,
        locationId: item.locationId || null,
        paidPlanId: splitEnabled ? null : pricing.selectedPackageId || null,
        voucherId: splitEnabled ? null : pricing.selectedVoucherId || null,
        splitSeats: splitEnabled ? splitSeats : null
      });

      clearCart();
      sessionStorage.setItem("widget_slug", slug);

      track("booking_submitted", {
        kind: item.type,
        service_id: item.serviceId || null,
        amount: displayTotal,
        currency: displayCurrency,
        used_package: !splitEnabled && !!pricing.selectedPackageId,
        used_voucher: !splitEnabled && !!pricing.selectedVoucherId,
        split: splitEnabled,
        next: result.requiresHold && result.holdToken ? "card_hold" : result.chargeId ? "online_payment" : "confirmed"
      });

      // Split booking: no charge — the host authorizes a card hold instead.
      if (result.requiresHold && result.holdToken) {
        navigate(`/checkout/telr-hold/${result.holdToken}`);
        return;
      }

      if (result.chargeId) {
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }

      // Package/voucher fully covered the booking — no online charge. Show the
      // invoice-style confirmation instead of dropping the client on the dashboard.
      if (result.invoiceId) {
        navigate(`/checkout/confirmation/${result.invoiceId}`);
        return;
      }

      navigate(`/${slug}/user`);
    } catch (err) {
      track("booking_failed", { kind: item.type, service_id: item.serviceId || null, reason: err?.code || "unknown" });
      // The slot became unbookable while the page was open — it entered the
      // club's minimum-notice window (LeadTimeError) or would now strand an
      // unbookable gap (GapPolicyError). Either way the time is gone, not the
      // booking attempt; send them back to repick.
      if (err.code === "LeadTimeError" || err.code === "GapPolicyError") {
        setError("This time just became unavailable — please go back and pick another time.");
      } else if (err.code === "BookingWindowError") {
        // Too far ahead, not gone — the club hasn't opened bookings for that
        // date/time yet (stale page or a shared link past the booking window).
        setError(err.message || "This time isn't open for online booking yet — please pick an earlier date.");
      } else if (err.code === "AvailabilityError") {
        setError(buildAvailabilityMessage(err));
      } else if (err.code === "ConflictError" || err.status === 409) {
        // The session's last seat was taken between opening the link and paying —
        // the server's atomic seat guard rejected the join before any charge.
        setError("Sorry, this session just filled up — its last seat was booked by someone else.");
      } else {
        setError(err.message || "Failed to complete booking. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // With split on, credits/membership are inapplicable (server rejects them),
  // so the summary shows the plain court total and a note replaces the panel.
  // A claimed seat on someone else's split booking: same card-only rule as the
  // host's side, so the same notice explains why no credits are offered.
  const isSplitSeat = !!primaryItem?.split;

  const splitCreditsNote = (
    // Inset only where it sits INSIDE the summary card (md+). On mobile the
    // summary drops its chrome and renders this alone, so the same inset would
    // make the notice narrower than the split card above it.
    <Box sx={{ p: { xs: 0, md: 3 }, pb: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          mb: 2,
          p: 1.75,
          borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.08)
        }}
      >
        <WarningAmberIcon sx={{ fontSize: "1.125rem", color: "warning.main", flexShrink: 0, mt: "0.125rem" }} />
        <Typography variant="caption" sx={{ display: "block", lineHeight: 1.5, color: "text.primary" }}>
          {isSplitSeat
            ? "Packages, vouchers and member pricing can't be used for a split-booking spot — each player pays their own share directly."
            : "Packages, vouchers and member pricing can't be combined with a split booking — each player pays their own share."}
        </Typography>
      </Box>
    </Box>
  );

  const itemCards = items.map((item, index) => (
    <React.Fragment key={index}>
      {isMobile && index > 0 && <Divider />}
      <CheckoutItemCard
        item={item}
        displayCurrency={displayCurrency}
        sportIcon={resolveSportIcon(item)}
        onRemove={() => handleRemoveItem(index)}
        bookingForName={index === 0 ? bookingForName : ""}
        embedded={isMobile}
      />
    </React.Fragment>
  ));

  const policiesPanel = (
    <CheckoutPoliciesPanel
      policies={policies}
      locationPolicies={locationPolicies}
      locationId={primaryItem?.locationId}
      embedded={isMobile}
      isCourt={primaryItem?.type === "court"}
      clubRules={checkoutRulesForCartItem(saas?.widgetRules, primaryItem, services)}
    />
  );

  // Mobile: the booking and the policies that govern it are one card; credits
  // stay in their own card below.
  const bookingCard = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: 1,
        borderColor: (t) => alpha(t.palette.divider, 0.6),
        backgroundColor: "background.paper",
        overflow: "hidden"
      }}
    >
      {itemCards}
      <Divider />
      {policiesPanel}
    </Paper>
  );

  const orderSummary = (
    <CheckoutOrderSummary
      displayCurrency={displayCurrency}
      primaryItem={primaryItem}
      membershipApplies={splitEnabled ? false : pricing.membershipApplies}
      membershipSaving={splitEnabled ? 0 : pricing.membershipSaving}
      isPackageApplied={splitEnabled ? false : pricing.isPackageApplied}
      packageSessionValue={splitEnabled ? 0 : pricing.packageSessionValue}
      voucherUsed={splitEnabled ? 0 : pricing.voucherUsed}
      selectedVoucher={pricing.selectedVoucher}
      total={displayTotal}
      error={error}
      submitting={submitting}
      onCheckout={handleCheckout}
      onClearCart={() => setConfirmClearOpen(true)}
      hideCta={isMobile}
      hideTotal={isMobile}
      totalLabel={splitEnabled ? "Card hold" : "Total"}
      totalSubLabel={splitEnabled ? `${displayCurrency}${splitShare.toFixed(2)} each — held now, not charged` : ""}
      hideClearCart={isMobile}
      canSubmit={termsAccepted}
      creditsSlot={
        splitEnabled || isSplitSeat ? (
          splitCreditsNote
        ) : hasCreditsToApply(creditsPanelProps) ? (
          <CheckoutCreditsPanel embedded {...creditsPanelProps} />
        ) : null
      }
      creditsSlotSelfContained={splitEnabled || isSplitSeat}
      termsSlot={<TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} disabled={submitting} />}
    />
  );

  const splitPanel =
    canSplit && isAuthenticated ? (
      <CheckoutSplitPanel
        enabled={splitEnabled}
        onToggle={handleSplitToggle}
        seats={splitSeats}
        onSeatsChange={setSplitSeats}
        totalAmount={splitTotal}
        currency={displayCurrency}
        item={primaryItem}
        seatLimits={seatLimits}
      />
    ) : null;

  return (
    <Box sx={{ maxWidth: "68.75rem", mx: "auto" }}>
      <Stack spacing={1.5} sx={{ mb: { xs: 3, md: 4 } }}>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: "1.125rem" }} />}
          sx={{
            alignSelf: "flex-start",
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontSize: "0.75rem",
            fontWeight: 700,
            px: 0,
            "&:hover": { background: "transparent", color: "primary.main" }
          }}
        >
          Back to Selection
        </Button>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "1.375rem", md: "1.75rem" },
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            color: "text.primary"
          }}
        >
          Review Your Booking
        </Typography>
      </Stack>

      {isEmpty ? (
        <Alert
          severity="error"
          action={
            <Button color="error" size="small" onClick={() => navigate(`/${slug}`)}>
              Browse Options
            </Button>
          }
          sx={{ borderRadius: 3 }}
        >
          Your cart is empty.
        </Alert>
      ) : isMobile ? (
        // Single column: the booking and its policies share one card, credits get
        // their own. The two-column Grid's spacing/flex overflows here, so mobile
        // uses a plain Stack. The CTA lives in the sticky bar, so the agreement
        // sits last, right above it.
        <Stack spacing={3}>
          {bookingCard}
          {splitPanel}
          {orderSummary}
        </Stack>
      ) : (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={7} sx={{ display: "flex" }}>
            <Stack spacing={3} sx={{ flex: 1 }}>
              {itemCards}
              {splitPanel}
              {policiesPanel}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5} sx={{ display: "flex" }}>
            {orderSummary}
          </Grid>
        </Grid>
      )}

      {/* Mobile: the primary action rides in a fixed bottom bar (in place of the
          bottom nav, which the layout hides on this route), so Total + Complete
          Booking stay in reach without scrolling past items and policies. The
          agreement rides with them — it gates the button, so it can't be left
          somewhere the client scrolls past. */}
      {isMobile && !isEmpty && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            px: 2,
            pt: 1,
            pb: "calc(0.75rem + env(safe-area-inset-bottom))",
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            boxShadow: "0 -6px 24px rgba(26,26,26,0.1)"
          }}
        >
          {/* Pull back the Checkbox's own padding so the box lines up with "Total" below. */}
          <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} disabled={submitting} sx={{ ml: -0.75 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}>
            <Box sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                {splitEnabled ? "Card hold" : "Total"}
              </Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                {displayCurrency}
                {displayTotal.toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleCheckout}
              disabled={submitting || !termsAccepted}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: 2,
                fontSize: "0.875rem",
                fontWeight: 700,
                boxShadow: (t) => `0 6px 18px ${alpha(t.palette.primary.main, 0.22)}`
              }}
            >
              {submitting ? "Processing..." : "Complete Booking"}
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear your cart?</DialogTitle>
        <DialogContent>
          <DialogContentText>This removes everything in your cart and takes you back to the start.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)} sx={{ textTransform: "none" }}>
            Keep booking
          </Button>
          <Button color="error" onClick={handleClearCart} sx={{ textTransform: "none" }}>
            Clear cart
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CheckoutView;
